import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Helmet } from 'react-helmet'
import { push } from 'connected-react-router'
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons'
import clsx from 'clsx'

import Button from '../../utils/Button'
import Card from '../../utils/Card'

import { fetchFacility } from '../../../actions/facilitiesActions'
import { matchingSiteCount } from '../../../actions/requestFormActions'

import { HeaderLarge as Header } from '../../utils/Header'

import OutOfOrder from './OutOfOrder'
import Description from './Description'
import Details from './Details'
import Content from './Content'
import FourOhFour from './FourOhFour'

import { AVAILABILITY_REQUEST } from '../../../utils/constants'

const ShowSegment = ({ className, children }) => <div className={clsx(className, 'w-full')}>{children}</div>

const Show = ({ match, ...props }) => {
  const dispatch = useDispatch()
  const fetching = useSelector(store => store.facilities.fetching)
  const facility = useSelector(store => store.facilities.facility)
  const facilityError = useSelector(store => store.facilities.error)

  useEffect(() => {
    dispatch(fetchFacility(match.params.id))
  }, [match.params.id])

  useEffect(() => {
    if (facility.id) {
      dispatch(matchingSiteCount({ ...AVAILABILITY_REQUEST.initialValues, facilityId: facility.id, type: undefined, ignoreAda: false }))
    }
  }, [facility])

  const newRequest = () => {
    dispatch(push(`/new/${facility.slug}`))
  }

  const metaDesc = useMemo(() => {
    return `Campsites at ${facility.name} sold out? Let us help you get a reservation! Wandering Labs searches for openings and alerts you when a campsite is canceled.`
  }, [facility])

  if (facilityError !== null) {
    return <FourOhFour />
  }

  return (
    <>
      <div className="md:flex space-x-0 md:space-x-4">
        <ShowSegment className="md:w-2/3 lg:w-1/2">
          <Header icon={faMapMarkerAlt} header={facility.name} subHeader={facility.subName} data-id={facility.id} />
          <OutOfOrder />
          <Description />

          <div className="mt-6">
            <Details />
          </div>

          <p></p>
        </ShowSegment>

        <ShowSegment className="md:w-1/3 lg:w-1/2 mt-12 md:mt-0">
          <Card color="gray" className="prose-xl">
            <p className="font-semibold">
              Campsites at {facility.name} all sold out? Reservations full? No availability?{' '}
            </p>
            <p>Create a new request with our service and we will alert you if a campsite becomes available</p>
            <p>Try it for free! Or become a Premium member and receive text alerts</p>
            <Button onClick={newRequest} color="green" className="flex-none">
              Create Availability Request
            </Button>
          </Card>
        </ShowSegment>
      </div>
      {!fetching && <Content tab={match.params.tab} />}
      <Helmet>
        <title>{`${facility.name} - Wandering Labs - wanderinglabs.com - Campsite reservation and cancellation notifications`}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={`https://wanderinglabs.com/f/${facility.slug}/`} />
        <meta
          property="og:title"
          content={`${facility.name} - Wandering Labs - wanderinglabs.com - Campsite reservation and cancellation notifications`}
        />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={`https://wanderinglabs.com/f/${facility.slug}/`} />
      </Helmet>
    </>
  )
}

export default Show
